import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowLeft, ArrowRight } from 'lucide-react';
import WorldMap from './WorldMap';
import BrandDirectory from './BrandDirectory';
import Reveal, { SectionHeader } from '../Reveal';
import { CLUSTERS, PANES } from '../../data/markets';
import { lenisRef } from '../../lib/atlas';

function BrandInfo({ brand }) {
  return (
    <motion.div
      key={brand.id}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      data-testid="brand-info"
    >
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-tangerine">
        {brand.country}
      </p>
      <h3 className="mt-3 font-syne font-extrabold text-2xl text-white tracking-tight" data-testid="brand-panel-name">
        {brand.name}
      </h3>
      {brand.role && (
        <div className="mt-5">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">Role</p>
          <p className="mt-1.5 text-sm text-slate-200 leading-relaxed">{brand.role}</p>
        </div>
      )}
      {brand.skills.length > 0 && (
        <div className="mt-4">
          <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-slate-500">Skills</p>
          <p className="mt-1.5 text-sm text-violettext leading-relaxed">
            {brand.skills.join(' · ')}
          </p>
        </div>
      )}
      {brand.note && (
        <p className="mt-4 text-[13px] italic text-slate-500 leading-relaxed">{brand.note}</p>
      )}
      {brand.projectId && (
        <Link
          to={`/work#p-${brand.projectId}`}
          data-testid={`brand-panel-link-${brand.id}`}
          className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-300 hover:text-tangerine transition-colors"
        >
          View on the timeline
          <ArrowRight size={12} />
        </Link>
      )}
    </motion.div>
  );
}

export default function MapSection({ isMobile, reducedMotion }) {
  const [selectedId, setSelectedId] = useState(null);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [hoverBrandId, setHoverBrandId] = useState(null);
  const panelRef = useRef(null);
  const selected = CLUSTERS.find((c) => c.id === selectedId) || null;
  const activeBrand = selected?.brands.find((b) => b.id === (hoverBrandId || activeBrandId)) || null;

  const handleSelect = (id) => {
    setSelectedId(id);
    setActiveBrandId(null);
    setHoverBrandId(null);
    if (isMobile) {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="market-map-container"]');
        if (!el) return;
        if (lenisRef.current) lenisRef.current.scrollTo(el, { duration: 1.1 });
        else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 60);
    }
  };

  const activateBrand = (id) => {
    setActiveBrandId((prev) => (prev === id ? null : id));
    if (id !== null && isMobile) {
      setTimeout(() => {
        const el = panelRef.current;
        if (!el) return;
        if (lenisRef.current) lenisRef.current.scrollTo(el, { duration: 1 });
        else el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 90);
    }
  };

  const backToMap = () => {
    setSelectedId(null);
    setActiveBrandId(null);
    setHoverBrandId(null);
  };

  return (
    <section id="explore" className="px-6 lg:px-16 py-20 lg:py-28" data-testid="section-explore">
      <div className="max-w-[1400px] mx-auto">
        <SectionHeader eyebrow="Explore — The Market Map" title="THE FOUR MARKETS" />
        <Reveal delay={0.2}>
          <p className="mt-8 max-w-xl text-sm sm:text-base text-slate-400 leading-relaxed">
            Over a decade of work across four market clusters. Select a market to open its
            brand directory — every logo carries a role and a skill set.
          </p>
        </Reveal>

        <motion.div
          initial={{ opacity: 0, scale: reducedMotion ? 1 : 1.02 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 relative border border-white/[0.07] bg-deep/30"
          data-testid="market-map-container"
        >
          <AnimatePresence mode="wait">
            {!selected ? (
              <motion.div
                key="map-view"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, scale: 0.99 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
              >
                {isMobile ? (
                  <div
                    className="flex overflow-x-auto no-scrollbar snap-x snap-mandatory"
                    data-testid="mobile-map-panes"
                  >
                    {PANES.map((pane) => (
                      <div key={pane.id} className="w-full shrink-0 snap-center relative">
                        <WorldMap
                          views={[pane.id]}
                          selectedId={selectedId}
                          hoverId={null}
                          onSelect={handleSelect}
                          onHover={() => {}}
                          isMobile
                        />
                        <span className="absolute top-3 left-4 font-mono text-[9px] uppercase tracking-[0.3em] text-slate-600">
                          {pane.caption}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <WorldMap
                    views={['world']}
                    selectedId={selectedId}
                    hoverId={null}
                    onSelect={handleSelect}
                    onHover={() => {}}
                    isMobile={false}
                  />
                )}
              </motion.div>
            ) : (
              <motion.div
                key={selected.id}
                initial={{ opacity: 0, scale: reducedMotion ? 1 : 1.012 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.995 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className={`grid ${isMobile ? '' : 'lg:grid-cols-12'}`}
                data-testid={`brand-directory-view-${selected.id}`}
              >
                <div className={isMobile ? '' : 'lg:col-span-8 lg:border-r border-white/[0.06]'}>
                  <BrandDirectory
                    cluster={selected}
                    isMobile={isMobile}
                    reducedMotion={reducedMotion}
                    activeBrandId={activeBrandId}
                    onActivate={activateBrand}
                    hoverId={hoverBrandId}
                    onHover={setHoverBrandId}
                  />
                </div>

                <div
                  ref={panelRef}
                  className={`${isMobile ? 'p-5 border-t border-white/[0.06]' : 'lg:col-span-4 p-8'} min-h-[220px]`}
                  data-testid="brand-panel"
                >
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-ultraviolet">
                      {selected.code} — Brand directory
                    </p>
                    {isMobile && activeBrand && (
                      <button
                        data-testid="brand-panel-close"
                        onClick={() => setActiveBrandId(null)}
                        aria-label="Close brand details"
                        className="text-slate-500 hover:text-white transition-colors p-1"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  <div className="mt-5 min-h-[140px]">
                    <AnimatePresence mode="wait">
                      {activeBrand ? (
                        <BrandInfo key={activeBrand.id} brand={activeBrand} />
                      ) : (
                        <motion.p
                          key="hint"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          className="text-sm text-slate-500 leading-relaxed max-w-xs"
                        >
                          {isMobile
                            ? 'Tap a brand to see the role and skills behind it.'
                            : 'Hover a brand to preview its role — click to pin it here.'}
                        </motion.p>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {selected && (
            <button
              data-testid="map-back-button"
              onClick={backToMap}
              className="absolute top-4 left-4 z-10 flex items-center gap-2 px-3.5 py-2 bg-void/80 backdrop-blur border border-white/15 hover:border-tangerine/70 transition-colors duration-300"
            >
              <ArrowLeft size={12} className="text-tangerine" />
              <span className="font-mono text-[9px] uppercase tracking-[0.25em] text-white">
                All markets
              </span>
            </button>
          )}
        </motion.div>

        {!selected && (
          <>
            {isMobile && (
              <div className="mt-6 grid grid-cols-2 gap-2.5" data-testid="mobile-cluster-chips">
                {CLUSTERS.map((c) => (
                  <button
                    key={c.id}
                    data-testid={`cluster-chip-${c.id}`}
                    onClick={() => handleSelect(c.id)}
                    className="px-4 py-3 border border-white/10 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400 hover:border-tangerine/70 hover:text-tangerine transition-colors duration-300"
                  >
                    {c.code} · {c.label}
                  </button>
                ))}
              </div>
            )}
            <Reveal delay={0.1}>
              <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600" data-testid="map-hint">
                {isMobile
                  ? 'Swipe between the two map panes — tap a marker or label'
                  : 'Select a market — click a marker or a region label'}
              </p>
            </Reveal>
          </>
        )}
      </div>
    </section>
  );
}
